import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  OnDestroy,
  NgZone,
  signal,
  computed,
  input,
  output,
  inject,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';

@Component({
  selector: 'app-friendly-captcha-wrapper',
  templateUrl: './friendly-captcha-wrapper.html',
  styleUrls: ['./friendly-captcha-wrapper.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FriendlyCaptchaWrapperComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FriendlyCaptchaWrapperComponent),
      multi: true,
    },
  ],
})
export class FriendlyCaptchaWrapperComponent
  implements AfterViewInit, OnDestroy, ControlValueAccessor, Validator
{
  sitekey = input.required<string>();
  apiEndpoint = input<'eu' | 'global' | string>('eu');
  theme = input<'light' | 'dark'>('light');
  start = input<'auto' | 'focus' | 'none'>('none');
  lang = input<string | undefined>();
  formFieldName = input('captchaResponse');

  solved = output<string | null>();
  captchaError = output<unknown>();

  @ViewChild('mount', { static: true }) mountRef!: ElementRef<HTMLElement>;

  private zone = inject(NgZone);

  private sdk?: FriendlyCaptchaSDK;
  private widget: any;

  private completeHandler?: EventListener;
  private errorHandler?: EventListener;
  private expireHandler?: EventListener;

  private token = signal<string | null>(null);
  isSolved = computed(() => !!this.token());

  private _onChange?: (val: string | null) => void;
  private _onTouched?: () => void;
  private _onValidatorChange?: () => void;

  ngAfterViewInit(): void {
    if (!this.sitekey() || typeof window === 'undefined' || !this.mountRef?.nativeElement) {
      return;
    }

    try {
      this.sdk = new FriendlyCaptchaSDK();

      this.widget = this.sdk.createWidget({
        element: this.mountRef.nativeElement,
        sitekey: this.sitekey(),
        startMode: this.start(),
        apiEndpoint: this.apiEndpoint(),
        theme: this.theme(),
        language: this.lang(),
      });

      this.bindWidgetEvents();
    } catch (e) {
      console.error('[FC v2] createWidget failed', e);
      this.zone.run(() => {
        this.updateToken(null);
        this.captchaError.emit(e);
      });
    }
  }

  private bindWidgetEvents(): void {
    if (!this.widget) return;

    this.completeHandler = ((event: Event) => {
      const customEvent = event as CustomEvent<{ response: string }>;
      const response = customEvent?.detail?.response ?? null;

      this.zone.run(() => {
        this.updateToken(response);
      });
    }) as EventListener;

    this.errorHandler = ((event: Event) => {
      const customEvent = event as CustomEvent<{ error?: unknown }>;

      this.zone.run(() => {
        this.updateToken(null);
        this.captchaError.emit(customEvent?.detail?.error ?? customEvent);
      });
    }) as EventListener;

    this.expireHandler = ((_event: Event) => {
      this.zone.run(() => {
        this.updateToken(null);
      });
    }) as EventListener;

    this.widget.addEventListener('frc:widget.complete', this.completeHandler);
    this.widget.addEventListener('frc:widget.error', this.errorHandler);
    this.widget.addEventListener('frc:widget.expire', this.expireHandler);
  }

  private updateToken(token: string | null): void {
    this.token.set(token);
    this._onTouched?.();
    this._onChange?.(token);
    this._onValidatorChange?.();
    this.solved.emit(token);
  }

  // CVA
  writeValue(value: string | null): void {
    if (!value) {
      this.resetPuzzle();
    }
  }

  handleClick(): void {
    if (!this.isSolved()) {
      this.startPuzzle();
    }
  }

  registerOnChange(fn: (val: string | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  // Validator
  validate(_: AbstractControl): ValidationErrors | null {
    return this.token() ? null : { required: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  startPuzzle(): void {
    try {
      this.widget?.start?.();
    } catch (e) {
      console.error('[FC v2] start failed', e);
    }
  }

  resetPuzzle(): void {
    try {
      this.widget?.reset?.();
    } catch (e) {
      console.error('[FC v2] reset failed', e);
    } finally {
      this.token.set(null);
      this._onChange?.(null);
      this._onValidatorChange?.();
      this.solved.emit(null);
    }
  }

  ngOnDestroy(): void {
    try {
      if (this.widget && this.completeHandler) {
        this.widget.removeEventListener('frc:widget.complete', this.completeHandler);
      }
      if (this.widget && this.errorHandler) {
        this.widget.removeEventListener('frc:widget.error', this.errorHandler);
      }
      if (this.widget && this.expireHandler) {
        this.widget.removeEventListener('frc:widget.expire', this.expireHandler);
      }

      this.widget?.destroy?.();
    } catch (e) {
      console.error('[FC v2] destroy failed', e);
    }
  }
}
